import Select, {
  type ActionMeta,
  type FormatOptionLabelMeta,
  type SelectComponentsConfig,
  type StylesConfig,
} from '@databases/atlaskit/Select';
import { SelectColorPicker } from '@databases/color-picker';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import { type SelectOption } from '@databases/types/database';
import { pickNewColor } from '@databases/utils/database/field-types/select';
import { generateId } from '@databases/utils/utils';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const getOptionValue = (op: SelectOption) => op.id ?? op.label;
const getOptionLabel = (op: SelectOption) => op.label;

const styles: StylesConfig<SelectOption, boolean> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
};

function hasOption(options: SelectOption[], option: SelectOption): boolean {
  return options.some((op) => (op.id ?? op.label) === (option.id ?? option.label));
}

const CreateOptionContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
`;

export interface SelectPickerProps {
  options: SelectOption[];
  value?: string[];
  onSelect?: (addedValue: string, allSelectedValues: string[]) => void;
  onRemove?: (removedValues: string[], allSelectedValues: string[]) => void;
  isCreatable?: boolean;
  onCreate?: (option: SelectOption, allSelectedValues: string[]) => void;
  isMulti: boolean;
  isInvalid?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  preventOpenWhenNoOptions?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
}

const SelectPicker: FunctionComponent<SelectPickerProps> = ({
  options,
  value = Object.freeze([]) as never[],
  onSelect,
  onRemove,
  isCreatable = true,
  onCreate,
  isMulti,
  isInvalid = false,
  onFocus,
  onBlur,
  autoFocus,
  placeholder,
  preventOpenWhenNoOptions,
  appearance,
}) => {
  const [nextColor, setNextColor] = useState(pickNewColor(options));
  useEffect(() => {
    setNextColor(pickNewColor(options));
  }, [options]);

  const [inputValue, setInputValue] = useState('');
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  const { formatMessage } = useIntl();

  const resolvedOptions = useMemo(() => {
    const result = [...options];
    if (isCreatable) {
      const trimmed = inputValue.trim();
      const trimmedLower = inputValue.trim().toLowerCase();
      if (trimmed && !options?.some((o) => o.label.toLowerCase() === trimmedLower)) {
        result.unshift({ id: generateId(), label: trimmed, color: nextColor });
      }
    }
    return result;
  }, [inputValue, isCreatable, nextColor, options]);

  const resolvedValue = useMemo(
    () => options.filter((op) => value.includes(op.id ?? op.label)),
    [options, value]
  );

  const handleChange = useCallback(
    (
      selectState: ReadonlyArray<SelectOption> | SelectOption | null,
      actionMeta: ActionMeta<SelectOption>
    ) => {
      const allSelectedValues: SelectOption[] = Array.isArray(selectState)
        ? selectState
        : selectState
        ? [selectState]
        : [];
      const allSelectedValueIds = allSelectedValues.map((v) => v.id ?? v.label);
      switch (actionMeta.action) {
        case 'select-option': {
          const selectedOption = actionMeta.option
            ? actionMeta.option
            : allSelectedValues[0];
          if (selectedOption) {
            if (isCreatable && !hasOption(options, selectedOption)) {
              onCreate?.(selectedOption, allSelectedValueIds);
            }
            onSelect?.(selectedOption.id ?? selectedOption.label, allSelectedValueIds);
          } else {
            logError(
              new Error(
                `No value found for actionMeta.action ==== 'select-option' from SelectPicker`
              )
            );
          }
          break;
        }
        case 'remove-value':
        case 'pop-value': {
          const removedOption = actionMeta.removedValue;
          if (removedOption) {
            onRemove?.([removedOption.id ?? removedOption.label], allSelectedValueIds);
          }
          break;
        }
        case 'deselect-option': {
          const deselectedOption = actionMeta.option;
          if (deselectedOption) {
            onRemove?.(
              [deselectedOption.id ?? deselectedOption.label],
              allSelectedValueIds
            );
          } else {
            logError(
              new Error(
                `No value found for actionMeta.action ==== 'deselect-option' from SelectPicker`
              )
            );
          }
          break;
        }
        case 'clear': {
          const optionsToRemove = actionMeta.removedValues.map((v) => v.id ?? v.label);
          onRemove?.(optionsToRemove, allSelectedValueIds);
          break;
        }
        default: {
          logError(
            new Error(`Unexpected actionMeta from SelectPicker: ${actionMeta.action}`)
          );
        }
      }
    },
    [isCreatable, onSelect, onRemove, onCreate, options]
  );

  const components: SelectComponentsConfig<SelectOption, boolean> = useMemo(
    () => ({
      ...(preventOpenWhenNoOptions && resolvedOptions.length === 0
        ? { DropdownIndicator: () => null }
        : {}),
      MultiValue: ({ data, removeProps }) => {
        return (
          <SelectOptionTag option={data} isRemovable onRemove={removeProps.onClick} />
        );
      },
    }),
    [preventOpenWhenNoOptions, resolvedOptions.length]
  );

  const formatOptionLabel = useCallback(
    (option: SelectOption, meta: FormatOptionLabelMeta<SelectOption>) => {
      if (meta.context === 'menu' && !hasOption(options, option)) {
        return (
          <CreateOptionContainer>
            {formatMessage(messages['field-settings.select-picker-create-text'])}
            {
              // Intentionally adding whitespace
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              ' '
            }
            <SelectOptionTag option={option} />
            <SelectColorPicker
              value={option.color}
              onChange={(color) => {
                setNextColor(color);
              }}
              stopPropagation
              size={24}
            />
          </CreateOptionContainer>
        );
      } else if (meta.context === 'menu' || !isMulti) {
        return <SelectOptionTag option={option} />;
      } else {
        return option.label;
      }
    },
    [formatMessage, isMulti, options]
  );

  return (
    <Select<SelectOption, boolean>
      classNamePrefix="select-option-picker"
      options={resolvedOptions}
      value={resolvedValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      placeholder={
        placeholder ??
        (isCreatable
          ? formatMessage(messages['select-or-create-text.placeholder'])
          : formatMessage(messages['select-dropdown.placeholder']))
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      isInvalid={isInvalid}
      isMulti={isMulti}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      menuIsOpen={
        preventOpenWhenNoOptions && resolvedOptions.length === 0 ? false : undefined
      }
      isClearable={!isMulti}
      components={components}
      formatOptionLabel={formatOptionLabel}
      hideSelectedOptions={false}
      defaultMenuIsOpen={
        preventOpenWhenNoOptions && resolvedOptions.length === 0
          ? false
          : autoFocus && !resolvedValue.length
      }
      styles={styles}
      appearance={appearance}
    />
  );
};

export default SelectPicker;
